<script setup lang="ts">
import { ref, onMounted, provide, computed, watch } from 'vue'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { usePartnerStore } from '@/stores/usePartnerStore'
import { useOpportunityStore } from '@/stores/useOpportunityStore'
import { usePartnerAttributionStore } from '@/stores/usePartnerAttributionStore'
import PartnerDetails from '@/components/features/partners/PartnerDetails.vue'
import PartnerTitleBlock from '@/components/features/partners/PartnerTitleBlock.vue'
import CompanyList from '@/components/features/companies/CompanyList.vue'
import OpportunityList from '@/components/features/opportunities/OpportunityList.vue'
import PartnerEventList from '@/components/features/partners/PartnerEventList.vue'
import { HomeIcon } from '@heroicons/vue/24/solid'
import type { Context } from '@/utils/types'
import { useToast } from 'vue-toast-notification'
import { useEventStore } from '@/stores/useEventStore'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ContactTable from '@/components/features/contact/ContactTable.vue'
import ContactCreateModal from '@/components/features/contact/ContactCreateModal.vue'
import ServicesSyncPanel from '@/components/features/services/ServicesSyncPanel.vue'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import PartnerTodoList from '@/components/features/partners/PartnerTodoList.vue'



const route = useRoute()
const router = useRouter()

const partnerStore = usePartnerStore()
const opportunityStore = useOpportunityStore()
const eventStore = useEventStore()
const { partnerOpportunities } = storeToRefs(opportunityStore)
const partnerAttributionStore = usePartnerAttributionStore()
const { partner, childsCompanyUuid, companies } = storeToRefs(partnerStore)
const $toast = useToast()
const boostCoreStore = useBoostCorePermissionStore()
const isSuperAdmin = computed(() => boostCoreStore.isSuperAdmin)

const pendingTodosCount = ref(0)

const partnerCompany = computed(() =>
  childsCompanyUuid.value && partner.value
    ? { uuid: childsCompanyUuid.value, name: partner.value.name }
    : null
)

const loadPartnerData = (id: number) => {
  // avoid reloading the same partner data
  if (partner.value?.id === id) return

  try {
     partnerStore.searchId(id)
     partnerStore.fetchCompanyByOdooRef(id)
     partnerStore.getContactsByParentId(id)
     opportunityStore.getOpportunitiesByParentId(id)
     partnerAttributionStore.getPartnerAttributionByPartnerId(id)
     partnerStore.getPartnersByParentId(id)
  } catch (error) {
    $toast.error('Erreur lors du chargement de la société')
    console.error('Erreur lors du chargement de la société :', error)
  }
}

onMounted(() => {
  const partnerId = parseInt(route.params.partnerId as string)
  loadPartnerData(partnerId)
  if (activeTab.value === 'event') {
    eventStore.getEventsByPartnerId(partnerId)
    eventStore.getEventsSubordinatesId()
    eventStore.getMyEvents()
  }
})

onBeforeRouteUpdate((to, from, next) => {
  const newPartnerId = parseInt(to.params.partnerId as string)
  loadPartnerData(newPartnerId)
  next()
})

const context: Context = 'partner'

provide('context', context)

const activeTab = ref((route.params.tab as string) || 'informations')

watch(
  () => route.params.tab,
  (newTab) => {
    activeTab.value = (newTab as string) || 'informations'
  },
)

const companiesLabel = computed(() => {
  const countCompanies = companies.value?.length ?? 0
  return `Filiales (${countCompanies})`
})

const todosLabel = computed(() =>
  pendingTodosCount.value > 0 ? `Tâches (${pendingTodosCount.value})` : 'Tâches'
)

const tabs = computed(() => [
  { name: 'informations', label: 'Informations', icon: HomeIcon },
  { name: 'contacts', label: 'Contacts' },
  { name: 'affaires', label: 'Affaires' },
  { name: 'event', label: 'Rendez-vous' },
  { name: 'todos', label: todosLabel.value },
  { name: 'company', label: companiesLabel.value },
  ...(isSuperAdmin.value ? [{ name: 'services', label: 'Services (Super Admin)' }] : []),
])
watch(
  () => activeTab.value,
  (tab) => {
    if (tab === 'event') {
      eventStore.getEventsByPartnerId(parseInt(route.params.partnerId as string))
      eventStore.getEventsSubordinatesId()
      eventStore.getMyEvents()
    }
    if (tab === 'contacts') {
      partnerStore.getContactsByParentId(parseInt(route.params.partnerId as string))
    }
  },
)
</script>

<template>
  <div class="w-full flex flex-col gap-4 p-8">
    <div class="flex flex-col gap-12">
      <Bs2Title title="Fiche client" />
    </div>
    <PartnerTitleBlock
      v-if="partner"
      :partner="partner"
      :company="partnerCompany"
    ></PartnerTitleBlock>
    <div class="skeleton opacity-30 w-full h-52" v-else></div>
    <div class="card bg-base-100">
      <div
        class="tabs tabs-boxed m-6 p-2 gap-2 flex justify-start self-center bg-background xl:w-4/5 rounded-full"
      >
        <a
          v-for="tab in tabs"
          :key="tab.name"
          :class="{
            'tab m-1 font-bold hover:bg-base-100 rounded-full': true,
            'tab-active bg-base-100': activeTab === tab.name,
          }"
          @click="router.push({ name: 'partner', params: { partnerId: route.params.partnerId, tab: tab.name } })"
        >
          <component v-if="tab.icon" :is="tab.icon" class="h-4 w-4" />
          <span v-else>{{ tab.label }}</span>
        </a>
      </div>

      <div class="p-4 px-10 bg-base-100 rounded-lg shadow-md">
        <div v-show="activeTab === 'informations'">
          <PartnerDetails v-if="partner" :partner="partner" />
          <div v-else class="skeleton opacity-30 w-full h-52"></div>
        </div>

        <div v-show="activeTab === 'contacts'">
          <ContactTable
            v-if="partnerStore.childs"
            :allow-sorting="true"
            :allow-filtering="true"
            :allow-export="true"
            :show-companies-filter="false"
            :partnerId="partner?.id || false"
            :rows="partnerStore.childs?.map(contact =>{
              return {
                ...contact,
                'civility': contact.title?.name || '',
                'firstname': contact.name.split(' ', 2)[0] ||'',
                'lastname': contact.name.split(' ', 2)[1] || '',
                'isSigner': contact.isSigner || false,
                'email': contact.email || '',
                'phone': contact.phone || '',
                'mobile': contact.mobile || '',
                'function': contact._function || '',
              }
            }) || []"
            :isLoading="partnerStore.loadingContacts"
          >
            <template #button>
              <ContactCreateModal
                v-if="partner"
                :preselectedCompany="{ uuid: childsCompanyUuid || '', name: partner.name }"
                @created="partnerStore.getContactsByParentId(partner.id)"
              />
            </template>
          </ContactTable>
          <div v-else class="skeleton opacity-30 w-full h-52"></div>
        </div>

        <div v-show="activeTab === 'affaires'">
          <OpportunityList v-if="partnerOpportunities" :data="partnerOpportunities" />
          <div class="skeleton opacity-30 w-full h-52" v-else></div>
        </div>

        <div v-show="activeTab === 'event'">
          <PartnerEventList />
        </div>

        <div v-show="activeTab === 'todos'">
          <PartnerTodoList
            v-if="childsCompanyUuid && partnerCompany"
            :company-uuid="childsCompanyUuid"
            :company="partnerCompany"
            @count-changed="(n) => (pendingTodosCount = n)"
          />
          <div v-else class="skeleton opacity-30 w-full h-52"></div>
        </div>

        <div v-show="activeTab === 'company'">
          <CompanyList :data="companies || []" />
        </div>

        <div v-if="activeTab === 'services' && isSuperAdmin">
          <ServicesSyncPanel :company-uuid="childsCompanyUuid" />
        </div>
      </div>
    </div>
  </div>
</template>
